/**
 * Three-way merge of TipTap chapter docs using stable paragraph ids + text hunk merge.
 */

import { diffBlocksById, type BlockRef } from '../../blockDiff';
import {
  alignParagraphIdsToBase,
  ensureParagraphIds,
  listChapterParagraphs,
  replaceBlocks,
  type BlockPatch,
  type ChapterBlockNode,
  type ChapterDocContent,
} from '../../chapterParagraphs';
import { plainTextToParagraphNode, threeWayTextMerge } from './textHunkMerge';
import type {
  ChapterConflictHunk,
  ChapterConflictResolution,
  ThreeWayMergeInput,
  ThreeWayMergeResult,
} from '../types';

function nodeKey(node: unknown): string {
  try {
    return JSON.stringify(node);
  } catch {
    return String(node);
  }
}

function blockPlainText(node: ChapterBlockNode | null | undefined): string {
  if (!node) return '';
  const refs = listChapterParagraphs({ type: 'doc', content: [node] });
  return refs[0]?.text ?? '';
}

function toBlockRefs(doc: ChapterDocContent): BlockRef[] {
  return listChapterParagraphs(doc).map((p, order) => ({
    id: p.id,
    content: p.node,
    order,
  }));
}

function afterIdForInsert(doc: ChapterDocContent, insertIndex: number): string | null {
  const paras = listChapterParagraphs(doc);
  if (insertIndex <= 0) return null;
  return paras[insertIndex - 1]?.id ?? null;
}

function makeConflictId(blockId: string, kind: string): string {
  return `${kind}:${blockId}`;
}

/**
 * Merge `mine` and `theirs` against a common `base`.
 * Non-overlapping block edits auto-apply; overlapping edits become conflict hunks
 * (unless plain-text 3-way merge succeeds inside a modified paragraph).
 */
export function threeWayChapterMerge(input: ThreeWayMergeInput): ThreeWayMergeResult {
  // Ensure base has stable ids first, then align mine/theirs onto that id space.
  // Independent ensureParagraphIds() calls mint divergent UUIDs and cause the
  // id-keyed merge to concatenate whole chapters (duplicates/triplicates).
  const base = ensureParagraphIds(input.base);
  const mine = alignParagraphIdsToBase(base, input.mine);
  const theirs = alignParagraphIdsToBase(base, input.theirs);

  const baseTitle = input.baseTitle ?? '';
  const mineTitle = input.mineTitle ?? baseTitle;
  const theirsTitle = input.theirsTitle ?? baseTitle;

  let mergedTitle = baseTitle;
  if (mineTitle === theirsTitle) mergedTitle = mineTitle;
  else if (mineTitle !== baseTitle && theirsTitle === baseTitle) mergedTitle = mineTitle;
  else if (theirsTitle !== baseTitle && mineTitle === baseTitle) mergedTitle = theirsTitle;
  else if (mineTitle !== baseTitle && theirsTitle !== baseTitle && mineTitle !== theirsTitle) {
    // Title conflict: prefer mine for the doc; caller may surface separately.
    // We still treat body merge independently.
    mergedTitle = mineTitle;
  }

  const mineKey = nodeKey(mine);
  const theirsKey = nodeKey(theirs);
  const baseKey = nodeKey(base);
  if (mineKey === theirsKey) {
    return {
      merged: mine,
      mergedTitle: mineTitle === theirsTitle ? mineTitle : mergedTitle,
      conflicts: [],
      autoMergedBlockCount: 0,
      identical: true,
    };
  }
  if (mineKey === baseKey) {
    return {
      merged: theirs,
      mergedTitle: theirsTitle,
      conflicts: [],
      autoMergedBlockCount: listChapterParagraphs(theirs).length,
      identical: false,
    };
  }
  if (theirsKey === baseKey) {
    return {
      merged: mine,
      mergedTitle: mineTitle,
      conflicts: [],
      autoMergedBlockCount: listChapterParagraphs(mine).length,
      identical: false,
    };
  }

  const baseRefs = toBlockRefs(base);
  const mineDiff = diffBlocksById(baseRefs, toBlockRefs(mine));
  const theirsDiff = diffBlocksById(baseRefs, toBlockRefs(theirs));

  const mineById = new Map(mineDiff.map((d) => [d.blockId, d]));
  const theirsById = new Map(theirsDiff.map((d) => [d.blockId, d]));
  const baseById = new Map(baseRefs.map((b) => [b.id, b]));
  const mineParas = listChapterParagraphs(mine);
  const theirsParas = listChapterParagraphs(theirs);
  const mineParaById = new Map(mineParas.map((p) => [p.id, p]));
  const theirsParaById = new Map(theirsParas.map((p) => [p.id, p]));

  const allIds = new Set<string>([
    ...baseRefs.map((b) => b.id),
    ...mineParas.map((p) => p.id),
    ...theirsParas.map((p) => p.id),
  ]);

  const patches: BlockPatch[] = [];
  const conflicts: ChapterConflictHunk[] = [];
  let autoMergedBlockCount = 0;
  let orderHint = 0;

  for (const blockId of allIds) {
    const m = mineById.get(blockId);
    const t = theirsById.get(blockId);
    const baseRef = baseById.get(blockId);
    const minePara = mineParaById.get(blockId);
    const theirsPara = theirsParaById.get(blockId);

    const mineChanged = Boolean(m);
    const theirsChanged = Boolean(t);

    if (!mineChanged && !theirsChanged) continue;

    // Only mine changed
    if (mineChanged && !theirsChanged) {
      if (m!.kind === 'deleted') {
        patches.push({ blockId, node: null });
      } else if (m!.kind === 'inserted') {
        patches.push({
          blockId,
          node: (minePara?.node ?? (m!.proposedContent as ChapterBlockNode)) ?? null,
          afterBlockId: afterIdForInsert(mine, minePara?.index ?? 0),
          insertIndex: minePara?.index ?? null,
        });
      } else {
        patches.push({
          blockId,
          node: (minePara?.node ?? (m!.proposedContent as ChapterBlockNode)) ?? null,
        });
      }
      autoMergedBlockCount += 1;
      continue;
    }

    // Only theirs changed
    if (!mineChanged && theirsChanged) {
      if (t!.kind === 'deleted') {
        patches.push({ blockId, node: null });
      } else if (t!.kind === 'inserted') {
        patches.push({
          blockId,
          node: (theirsPara?.node ?? (t!.proposedContent as ChapterBlockNode)) ?? null,
          afterBlockId: afterIdForInsert(theirs, theirsPara?.index ?? 0),
          insertIndex: theirsPara?.index ?? null,
        });
      } else {
        patches.push({
          blockId,
          node: (theirsPara?.node ?? (t!.proposedContent as ChapterBlockNode)) ?? null,
        });
      }
      autoMergedBlockCount += 1;
      continue;
    }

    // Both changed
    const mineKind = m!.kind;
    const theirsKind = t!.kind;

    if (mineKind === 'deleted' && theirsKind === 'deleted') {
      patches.push({ blockId, node: null });
      autoMergedBlockCount += 1;
      continue;
    }

    if (mineKind === 'deleted' && theirsKind !== 'deleted') {
      conflicts.push({
        id: makeConflictId(blockId, 'edit_vs_delete'),
        blockId,
        kind: 'edit_vs_delete',
        baseNode: (baseRef?.content as ChapterBlockNode) ?? null,
        mineNode: null,
        theirsNode: (theirsPara?.node as ChapterBlockNode) ?? null,
        baseText: blockPlainText((baseRef?.content as ChapterBlockNode) ?? null),
        mineText: '',
        theirsText: theirsPara?.text ?? '',
        orderHint: orderHint++,
      });
      continue;
    }

    if (theirsKind === 'deleted' && mineKind !== 'deleted') {
      conflicts.push({
        id: makeConflictId(blockId, 'delete_vs_edit'),
        blockId,
        kind: 'delete_vs_edit',
        baseNode: (baseRef?.content as ChapterBlockNode) ?? null,
        mineNode: (minePara?.node as ChapterBlockNode) ?? null,
        theirsNode: null,
        baseText: blockPlainText((baseRef?.content as ChapterBlockNode) ?? null),
        mineText: minePara?.text ?? '',
        theirsText: '',
        orderHint: orderHint++,
      });
      continue;
    }

    const mineNode = (minePara?.node ?? (m!.proposedContent as ChapterBlockNode)) ?? null;
    const theirsNode = (theirsPara?.node ?? (t!.proposedContent as ChapterBlockNode)) ?? null;

    if (nodeKey(mineNode) === nodeKey(theirsNode)) {
      patches.push({
        blockId,
        node: mineNode,
        afterBlockId:
          mineKind === 'inserted' ? afterIdForInsert(mine, minePara?.index ?? 0) : undefined,
        insertIndex: mineKind === 'inserted' ? minePara?.index ?? null : undefined,
      });
      autoMergedBlockCount += 1;
      continue;
    }

    // Both modified (or both inserted differently) — try text 3-way
    const baseText = blockPlainText((baseRef?.content as ChapterBlockNode) ?? null);
    const mineText = minePara?.text ?? blockPlainText(mineNode);
    const theirsText = theirsPara?.text ?? blockPlainText(theirsNode);
    const textMerged = threeWayTextMerge(baseText, mineText, theirsText);

    if (textMerged != null && mineKind !== 'inserted') {
      const template = mineNode ?? theirsNode;
      const mergedNode = plainTextToParagraphNode(textMerged, template) as ChapterBlockNode;
      patches.push({ blockId, node: mergedNode });
      autoMergedBlockCount += 1;
      continue;
    }

    conflicts.push({
      id: makeConflictId(blockId, mineKind === 'inserted' ? 'both_inserted' : 'both_modified'),
      blockId,
      kind: mineKind === 'inserted' ? 'both_inserted_different' : 'both_modified',
      baseNode: (baseRef?.content as ChapterBlockNode) ?? null,
      mineNode,
      theirsNode,
      baseText,
      mineText,
      theirsText,
      orderHint: orderHint++,
    });
  }

  // Start from base, apply auto-merge patches. Conflicted blocks stay as base until resolved.
  let merged = replaceBlocks(base, patches);

  // Inserts that only exist on one side are in patches; both-inserted conflicts are not applied.

  // Title-only conflict flag is not in conflicts[]; body conflicts only.
  return {
    merged,
    mergedTitle,
    conflicts,
    autoMergedBlockCount,
    identical: false,
  };
}

/**
 * Apply user resolutions to a merge result, producing a final TipTap doc.
 */
export function applyConflictResolutions(
  mergeResult: ThreeWayMergeResult,
  resolutions: ChapterConflictResolution[]
): ChapterDocContent {
  const byId = new Map(resolutions.map((r) => [r.hunkId, r]));
  const patches: BlockPatch[] = [];

  for (const hunk of mergeResult.conflicts) {
    const res = byId.get(hunk.id);
    if (!res) continue;

    if (res.choice === 'mine') {
      patches.push({ blockId: hunk.blockId, node: hunk.mineNode });
    } else if (res.choice === 'theirs') {
      patches.push({ blockId: hunk.blockId, node: hunk.theirsNode });
    } else {
      if (res.combinedNode !== undefined) {
        patches.push({ blockId: hunk.blockId, node: res.combinedNode });
      } else if (typeof res.combinedText === 'string') {
        const template = hunk.mineNode ?? hunk.theirsNode ?? hunk.baseNode;
        patches.push({
          blockId: hunk.blockId,
          node: plainTextToParagraphNode(res.combinedText, template) as ChapterBlockNode,
        });
      } else {
        patches.push({ blockId: hunk.blockId, node: hunk.mineNode });
      }
    }
  }

  return replaceBlocks(mergeResult.merged, patches);
}
